"use client";

import * as React from "react";
import { CODEBASE } from "../../../data/viz";
import { useSize } from "../../../hooks";
import VizFrame from "./VizFrame";

type Node = {
  name: string;
  value: number;
  x: number;
  y: number;
  w: number;
  h: number;
};

/**
 * Squarified treemap, laid out by hand.
 *
 * d3-hierarchy would do this in three lines, but it is a whole dependency for
 * one flat level of nesting, and the squarify pass is short enough to own.
 */
function squarify(
  items: { name: string; value: number }[],
  x: number,
  y: number,
  w: number,
  h: number,
): Node[] {
  const total = items.reduce((s, d) => s + d.value, 0);
  if (total <= 0 || items.length === 0) return [];

  const scale = (w * h) / total;
  const out: Node[] = [];
  let rest = items.map((d) => ({ ...d, area: d.value * scale }));

  const worst = (row: { area: number }[], len: number) => {
    const sum = row.reduce((s, d) => s + d.area, 0);
    const max = Math.max(...row.map((d) => d.area));
    const min = Math.min(...row.map((d) => d.area));
    const len2 = len * len;
    const sum2 = sum * sum;
    return Math.max((len2 * max) / sum2, sum2 / (len2 * min));
  };

  while (rest.length) {
    const horizontal = w >= h;
    const len = horizontal ? h : w;
    const row: typeof rest = [];

    while (rest.length) {
      const candidate = [...row, rest[0]];
      if (row.length && worst(candidate, len) > worst(row, len)) break;
      row.push(rest.shift()!);
    }

    const rowArea = row.reduce((s, d) => s + d.area, 0);
    const thickness = rowArea / len;
    let offset = 0;

    for (const item of row) {
      const share = item.area / rowArea;
      if (horizontal) {
        out.push({
          name: item.name,
          value: item.value,
          x,
          y: y + offset,
          w: thickness,
          h: len * share,
        });
        offset += len * share;
      } else {
        out.push({
          name: item.name,
          value: item.value,
          x: x + offset,
          y,
          w: len * share,
          h: thickness,
        });
        offset += len * share;
      }
    }

    if (horizontal) {
      x += thickness;
      w -= thickness;
    } else {
      y += thickness;
      h -= thickness;
    }
  }

  return out;
}

export default function Treemap() {
  const [ref, size] = useSize<HTMLDivElement>();
  const [hover, setHover] = React.useState<string | null>(null);

  const w = Math.max(size.width - 24, 240);
  const h = Math.min(Math.max(Math.round(w * 0.52), 240), 380);

  const nodes = React.useMemo(
    () => squarify(CODEBASE as { name: string; value: number }[], 0, 0, w, h),
    [w, h],
  );

  const total = CODEBASE.reduce((s, d) => s + d.value, 0);
  const max = Math.max(...CODEBASE.map((d) => d.value));

  return (
    <VizFrame
      title="First-party C# by system"
      meta={`${total.toLocaleString()} lines · ${CODEBASE.length} systems`}
    >
      <div ref={ref} className="p-3">
        <svg
          width="100%"
          height={h}
          viewBox={`0 0 ${w} ${h}`}
          role="img"
          aria-label={`Treemap of the Swarmhold codebase. Largest systems: ${CODEBASE.slice(
            0,
            3,
          )
            .map((d) => `${d.name} at ${d.value.toLocaleString()} lines`)
            .join(", ")}.`}
        >
          {nodes.map((n) => {
            const dim = hover !== null && hover !== n.name;
            const strength = 0.14 + (n.value / max) * 0.55;
            const showLabel = n.w > 62 && n.h > 30;
            return (
              <g
                key={n.name}
                onMouseEnter={() => setHover(n.name)}
                onMouseLeave={() => setHover(null)}
                style={{ cursor: "crosshair" }}
              >
                <rect
                  x={n.x + 1}
                  y={n.y + 1}
                  width={Math.max(n.w - 2, 0)}
                  height={Math.max(n.h - 2, 0)}
                  rx={3}
                  fill="rgb(var(--accent))"
                  opacity={dim ? strength * 0.35 : strength}
                  style={{ transition: "opacity .15s" }}
                />
                {showLabel && (
                  <>
                    <text
                      x={n.x + 9}
                      y={n.y + 19}
                      className="pointer-events-none fill-[rgb(var(--ink))] text-[11px] font-medium"
                    >
                      {n.name}
                    </text>
                    <text
                      x={n.x + 9}
                      y={n.y + 33}
                      className="pointer-events-none fill-[rgb(var(--ink))] font-mono text-[10px] opacity-70"
                    >
                      {(n.value / 1000).toFixed(1)}k
                    </text>
                  </>
                )}
                <title>{`${n.name}: ${n.value.toLocaleString()} lines (${((n.value / total) * 100).toFixed(1)}%)`}</title>
              </g>
            );
          })}
        </svg>
      </div>
    </VizFrame>
  );
}
